import { useDispatch, useSelector } from "react-redux";
import { Modal, Box, useTheme, useMediaQuery } from "@mui/material";

import AiAssistant from "@/ai/AiAssistant";
import {
    selectProjectWorkshopStatus,
    closeProjectWorkshop,
} from "@/nav/navSlice";

/**
 * ProjectAgentWorkshop — dispatcher that decides which workshop to render.
 *
 * - With projectId: opens the project-scoped AiAssistant (sidebar, workflows)
 * - Without projectId: opens the global AiAssistant (no sidebar, single workflow)
 */
export default function ProjectAgentWorkshop() {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("md"));

    const workshopStatus = useSelector(selectProjectWorkshopStatus);
    const { open, activeAgent, projectId, projectName, taskSummary, threadId } =
        workshopStatus;

    const handleClose = () => {
        dispatch(closeProjectWorkshop());
    };

    if (!open) return null;

    // Global mode: TopNav "Project Quick Start" / "Bid Quick Start" / Dashboard card (no projectId)
    if (!projectId) {
        return (
            <Modal
                open={open}
                onClose={handleClose}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    MozOsxFontSmoothing: "grayscale",
                    WebkitFontSmoothing: "antialiased",
                }}
            >
                <Box
                    sx={{
                        width: isSmall ? "100%" : "94%",
                        height: isSmall ? "100%" : "86vh",
                        outline: "none",
                    }}
                >
                    <AiAssistant
                        modelType={null}
                        modelInstanceId={null}
                        modelLabel={null}
                        availableWorkflows={[]}
                        initialAgent={activeAgent || "intake"}
                        hideSidebar
                        onClose={handleClose}
                    />
                </Box>
            </Modal>
        );
    }

    // Project-scoped mode: Project page AI assistant
    return (
        <Modal
            open={open}
            onClose={handleClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                MozOsxFontSmoothing: "grayscale",
                WebkitFontSmoothing: "antialiased",
            }}
        >
            <Box
                sx={{
                    width: isSmall ? "100%" : "94%",
                    height: isSmall ? "100%" : "86vh",
                    outline: "none",
                }}
            >
                <AiAssistant
                    modelType="project"
                    modelInstanceId={projectId}
                    modelLabel={projectName}
                    availableWorkflows={[
                        "brief",
                        // "bid_create", // hidden for this branch
                        // "bid",        // hidden for this branch
                        "timeline",
                        "wrap_report",
                    ]}
                    initialAgent={activeAgent}
                    initialPrompt={taskSummary || ""}
                    onClose={handleClose}
                />
            </Box>
        </Modal>
    );
}
